import React from "react";
import { Container, Box, Typography, Grid, Card, CardContent, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import Carousel from "../components/Carousel";
import { productCategories } from "../data/products";
// import SEO from "../components/SEO";

const Section = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    {children}
  </motion.div>
);

function Home() {
  const theme = useTheme();

  const features = [
    {
      icon: "🏛️",
      title: "Trusted Since 1970",
      description: "Over 50 years of excellence in industrial packaging solutions"
    },
    {
      icon: "🤝",
      title: "Strong Vendor Network",
      description: "Extensive network of trusted suppliers and partners"
    },
    {
      icon: "🚚",
      title: "Timely Delivery",
      description: "Reliable and punctual delivery across India"
    },
    {
      icon: "💼",
      title: "Transparent Dealings",
      description: "Clear communication and honest business practices"
    },
    {
      icon: "💳",
      title: "Easy Payment Options",
      description: "Flexible payment terms for your convenience"
    },
    {
      icon: "⭐",
      title: "Client-First Approach",
      description: "Dedicated to exceeding customer expectations"
    }
  ];

  return (
    <>
      {/* Hero Carousel */}
      <Carousel />

      {/* Legacy of Trust and Quality Section */}
      <Section>
        <Box sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h2"
                  sx={{ mb: 3, fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Legacy of Trust and Quality
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Founded by Late Shri Sureshkumar Shah, Shree Balaji Corporation has built 
                  its name on reliability, ethics, and customer-first service since 1970. 
                  Today, under the leadership of Ashish Shah, we continue to excel in providing 
                  cotton and pressing industries packaging solutions from our base in Akola, Maharashtra.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6 }}>
                  With over five decades of experience, we've established ourselves as a leading 
                  supplier of PET straps for ginning, leather washers for ginning, bale covers, 
                  and strapping machines for baling, serving ginning and pressing industries across India.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/about"
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Read More About Us
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: 400,
                    backgroundImage: "url('/assets/about-2.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(255, 193, 7, 0.1))",
                    },
                  }}
                >
                  {/* Fallback if image doesn't load */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "4rem",
                      color: theme.palette.primary.main,
                      opacity: 0.3,
                    }}
                  >
                    🏭
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Section>

      {/* Product Categories Section */}
      <Section>
        <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                mb: 6,
                color: theme.palette.primary.main,
                fontWeight: 700,
              }}
            >
              Our Product Categories
            </Typography>

            <Grid container spacing={8} >
              {productCategories.map((category, index) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      component={RouterLink}
                      to={`/products?category=${category.id}`}
                      borderRadius={2}
                      sx={{
                        height: "100%",
                        textDecoration: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          height: 200,
                          backgroundImage: category.image ? `url(${category.image})` : 'none',
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundColor: category.image ? 'transparent' : theme.palette.background2.paper,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: category.image ? 'inherit' : '3rem',
                          color: category.image ? 'inherit' : 'white',
                        }}
                      >
                        {!category.image && '📦'}
                      </Box>
                      <CardContent sx={{ textAlign: "center", p: 3 }}>
                        <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                          {category.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {category.description}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          {category.products.length} Products
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: "center", mt: 6 }}>
              <Button
                component={RouterLink}
                to="/products"
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                View All Products
              </Button>
            </Box>
          </Container>
        </Box>
      </Section>

      {/* Why Choose Us Section */}
      <Section>
        <Box sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                mb: 6,
                color: theme.palette.primary.main,
                fontWeight: 700,
              }}
            >
              Why Choose Shree Balaji Corporation?
            </Typography>

            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        textAlign: "center",
                        p: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            fontSize: "3rem",
                            mb: 2,
                            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          component="h3"
                          sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Section>
    </>
  );
}

export default Home;
